import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle, Money, Clock, Camera, Eye } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { POSTER_TIERS } from "@/lib/data";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function BecomeAPoster() {
    const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", avg_status_views: "" });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/leads/poster", form);
            toast.success("Application received! We'll be in touch within 24 hours.");
            setSubmitted(true);
            setForm({ name: "", phone: "", email: "", city: "", avg_status_views: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-white" data-testid="poster-page">
            {/* Hero */}
            <section className="bg-[#0A192F] pt-32 md:pt-40 pb-20 relative overflow-hidden bg-grain">
                <div className="absolute top-1/4 right-0 w-96 h-96 rounded-full bg-[#D4AF37]/15 blur-3xl" />
                <div className="max-w-7xl mx-auto px-6 md:px-8 relative max-w-4xl">
                    <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">Earn With Us</div>
                    <h1 className="font-display text-4xl md:text-6xl font-bold text-white leading-tight">
                        Get paid to post ads on your <span className="text-gold-gradient">WhatsApp Status.</span>
                    </h1>
                    <p className="text-white/75 mt-5 max-w-2xl leading-relaxed">
                        CashFlow Ads pays individuals to share advertisements on their personal WhatsApp Statuses. Just keep the ad up for 24 hours, send a screenshot, and get paid.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        {["Real views only", "24-hour minimum", "Verified payouts"].map((t) => (
                            <span key={t} className="px-3 py-1.5 rounded-full bg-white/5 border border-[#D4AF37]/30 text-xs text-white/85">{t}</span>
                        ))}
                    </div>
                </div>
            </section>

            {/* Payout structure */}
            <section className="py-20 md:py-28">
                <div className="max-w-6xl mx-auto px-6 md:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">Payout Structure</div>
                        <h2 className="font-display text-3xl md:text-5xl font-bold text-[#0A192F]">More views, more cash.</h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {POSTER_TIERS.map((t, i) => (
                            <div key={i} className={`rounded-2xl p-6 card-lift text-center ${i === 4 ? "bg-[#0A192F] text-white border-2 border-[#D4AF37]" : "bg-[#F8FAFC] border border-slate-100"}`} data-testid={`payout-tier-${i}`}>
                                <Money size={28} weight="duotone" color={i === 4 ? "#D4AF37" : "#0A192F"} className="mx-auto" />
                                <div className={`font-display text-3xl font-bold mt-3 ${i === 4 ? "text-[#D4AF37]" : "text-[#0A192F]"}`}>{t.pay}</div>
                                <div className={`text-xs mt-1 ${i === 4 ? "text-white/70" : "text-slate-500"}`}>{t.range}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Requirements */}
            <section className="bg-[#F8FAFC] py-20 md:py-28">
                <div className="max-w-5xl mx-auto px-6 md:px-8">
                    <h2 className="font-display text-3xl md:text-4xl font-bold text-[#0A192F] mb-10 text-center">Poster Requirements</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        {[
                            { i: Clock, t: "Keep ad up for 24 hours", d: "Your status must remain live without interruption." },
                            { i: Camera, t: "Send screenshot proof", d: "We need a clean, unedited screenshot from your live account." },
                            { i: Eye, t: "Real views only", d: "We verify view counts. No artificial inflation." },
                            { i: CheckCircle, t: "No edited screenshots", d: "Authenticity is non-negotiable. Edited proof = banned account." },
                        ].map((r, i) => (
                            <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100 card-lift">
                                <r.i size={28} weight="duotone" color="#D4AF37" />
                                <h3 className="font-display text-lg font-semibold mt-3 text-[#0A192F]">{r.t}</h3>
                                <p className="text-sm text-slate-600 mt-1.5">{r.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Application form */}
            <section className="py-20 md:py-28">
                <div className="max-w-2xl mx-auto px-6 md:px-8">
                    <div className="text-center mb-10">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">Apply Now</div>
                        <h2 className="font-display text-3xl md:text-4xl font-bold text-[#0A192F]">Join the poster network.</h2>
                    </div>
                    {submitted ? (
                        <div className="bg-[#0A192F] text-white rounded-3xl p-10 text-center" data-testid="poster-success">
                            <CheckCircle size={56} weight="fill" color="#D4AF37" className="mx-auto" />
                            <h3 className="font-display text-2xl font-bold mt-4">Application received!</h3>
                            <p className="text-white/70 mt-2">Our team will contact you within 24 hours.</p>
                            <button onClick={() => setSubmitted(false)} className="mt-6 px-5 py-2.5 rounded-full bg-[#D4AF37] text-[#0A192F] font-semibold text-sm" data-testid="poster-submit-another">
                                Submit another
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="bg-white rounded-3xl p-8 md:p-10 border border-slate-200 shadow-xl space-y-5" data-testid="poster-form">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="p-name" className="text-[#0A192F] font-semibold">Full Name</Label>
                                    <Input id="p-name" required value={form.name} onChange={update("name")} className="mt-1.5" data-testid="poster-input-name" />
                                </div>
                                <div>
                                    <Label htmlFor="p-phone" className="text-[#0A192F] font-semibold">Phone</Label>
                                    <Input id="p-phone" required value={form.phone} onChange={update("phone")} className="mt-1.5" data-testid="poster-input-phone" />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="p-email" className="text-[#0A192F] font-semibold">Email</Label>
                                <Input id="p-email" type="email" required value={form.email} onChange={update("email")} className="mt-1.5" data-testid="poster-input-email" />
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="p-city" className="text-[#0A192F] font-semibold">City</Label>
                                    <Input id="p-city" required value={form.city} onChange={update("city")} className="mt-1.5" data-testid="poster-input-city" />
                                </div>
                                <div>
                                    <Label htmlFor="p-views" className="text-[#0A192F] font-semibold">Avg. Status Views</Label>
                                    <Input id="p-views" required placeholder="e.g. 500" value={form.avg_status_views} onChange={update("avg_status_views")} className="mt-1.5" data-testid="poster-input-views" />
                                </div>
                            </div>
                            <Button type="submit" disabled={submitting} className="w-full bg-[#D4AF37] hover:bg-[#e3c14a] text-[#0A192F] font-semibold py-6 rounded-full text-base" data-testid="poster-submit-button">
                                {submitting ? "Submitting..." : "Submit Application"}
                            </Button>
                        </form>
                    )}
                </div>
            </section>
        </div>
    );
}
